import { useState, useEffect, useCallback } from 'react';
// material
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  Container,
  Typography,
  TableContainer,
  tableCellClasses,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TableCell,
  Checkbox,
  Box,
  Select,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFoundAgreement from '../../components/SearchNotFoundAgreement';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import BrudCrumbs from '../../components/BreadCrumbs';
import { fDate } from '../../utils/formatTime';
import DataToolbar from './DataToolbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getBankDropdownData, saveApiData } from '../../store/Slice/Global/GlobalSlice';
import useDebounce from '../../utils/useDebounce';
import { getDataSourceList } from '../../store/Slice/DataSource/DataSourceSlice';
import useDataSourceSelector from '../../store/Slice/DataSource/DataSourceSelector';
import RevenueService from '../services/RevenueService';

// ----------------------------------------------------------------------
const TABLE_HEAD = [
  { id: 'datasetName', label: 'DATASET NAME', alignRight: false },
  { id: 'uploadMethod', label: 'IMPORT METHOD', alignRight: false },
  { id: 'noOfRecords', label: 'NO. OF RECORDS', alignRight: false },
  { id: 'noOfColumns', label: 'NO. OF COLUMNS', alignRight: false },
  { id: 'uploadedOn', label: 'UPLOADED ON', alignRight: false },
  { id: 'modifiedOn', label: 'MODIFIED ON', alignRight: false },
  { id: 'status', label: 'STATUS', alignRight: false },
  { id: 'action', label: 'ACTION', alignRight: false, align: 'center' }
];

const StyledTableRow = styled(TableRow)(() => ({
  backgroundColor: '#fff',
  '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
  '&:hover': { backgroundColor: '#f1f5f9 !important' },
  '&:last-child td, &:last-child th': { border: 0 }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    backgroundColor: '#ffffff',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9'
  }
}));

const SELECT_STATUS = {
  ACTIVE: {
    type_name: 'Active',
    type_code: 'ACTIVE	',
    type_id: 134
  },
  INACTIVE: {
    type_name: 'Inactive',
    type_code: 'INACTIVE	',
    type_id: 136
  },
  INCOMPLETE: {
    type_name: 'Incomplete',
    type_code: 'INCOMPLETE	',
    type_id: 138
  }
};

const getLabelColor = (status) => {
  const s = (status || '').toLowerCase();
  if (s.includes('inactive') || s.includes('reject') || s.includes('fail')) return 'error';
  if (s.includes('new') || s.includes('pend') || s.includes('incomplete')) return 'warning';
  if (
    s.includes('active') ||
    s.includes('success') ||
    s.includes('complet') ||
    s.includes('assign')
  )
    return 'success';
  return 'info';
};

export default function DataList() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectStatusValue, setSelectStatusValue] = useState(' ');
  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);
  const [filterParameter, setFilterParameter] = useState({
    bankId: null
  });
  const [hasNextPage, setHasNextPage] = useState(true);
  const [validateDialogOpen, setValidateDialogOpen] = useState(false);
  const [pendingValidateId, setPendingValidateId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const { DataSourceListData } = useDataSourceSelector();
  const { DownloadExportRecord } = RevenueService;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { LoginData } = useLoginSelector();
  const debouncedSearch = useDebounce(filterName, 1000);

  const handleOpen = useCallback((e) => setAnchorEl(e.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const getAgreementsListApi = useCallback(() => {
    let statusResult = null;

    if (selectStatusValue === 134) {
      statusResult = true;
    } else if (selectStatusValue === 136) {
      statusResult = false;
    }

    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        sortOrder: order,
        pageNumber: page + 1
      },
      filterModel: {
        status: statusResult,
        uploadMethod: filterParameter?.bankId || null
      }
    };

    dispatch(getDataSourceList(credentials));
  }, [
    LoginData,
    debouncedSearch,
    order,
    selectStatusValue,
    filterParameter,
    page,
    rowsPerPage,
    dispatch
  ]);

  useEffect(() => {
    getAgreementsListApi();
  }, [getAgreementsListApi]);

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [dispatch, LoginData]);

  useEffect(() => {
    getBankDropApi();
  }, [getBankDropApi]);

  const handleRequestSort = useCallback(
    (event, property) => {
      if (property !== 'action') {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
      }
    },
    [order, orderBy]
  );

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelecteds = DataSourceListData?.map((n) => n) || [];
        setSelected(newSelecteds);
        return;
      }
      setSelected([]);
    },
    [DataSourceListData]
  );

  useEffect(() => {
    setHasNextPage(DataSourceListData?.length >= rowsPerPage);
  }, [DataSourceListData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const handleClick = useCallback(
    (event, item) => {
      const selectedIndex = selected.indexOf(item);
      let newSelected = [];
      if (selectedIndex === -1) {
        newSelected = [...selected, item];
      } else {
        newSelected = selected.filter((s) => s !== item);
      }
      setSelected(newSelected);
    },
    [selected]
  );

  const handleOpenValidateDialog = useCallback((rowId) => {
    setPendingValidateId(rowId);
    setValidateDialogOpen(true);
  }, []);

  const handleConfirmValidate = useCallback(() => {
    setValidateDialogOpen(false);
    navigate(`/data/validate`, {
      state: { rowId: pendingValidateId }
    });
  }, [navigate, pendingValidateId]);

  const handleExport = async (type) => {
    console.log('type222', type);
    const credential = { exportType: type };
    try {
      const response = await DownloadExportRecord(credential, LoginData?.jwt);
      console.log('response222', response);
      const contentType = response.headers.get('content-type');
      const blob = await response.blob();
      let fileName = 'exportData';
      let mimeType = contentType;
      if (type === 'EXCEL') {
        fileName += '.xlsx';
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else if (type === 'PDF') {
        fileName += '.pdf';
        mimeType = 'application/pdf';
      }
      const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      handleClose();
    }
  };
  return (
    <Page title="Partner Management System || Data Source">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title, and Breadcrumb */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <StorageOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Data Source
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Manage datasets, import methods, and upload history.
              </Typography>
            </Box>
          </Stack>

          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <DataToolbar
            numSelected={selected.length}
            filterName={filterName}
            setFilterName={setFilterName}
            parent="data"
            selectStatusValue={selectStatusValue}
            setSelectStatusValue={setSelectStatusValue}
            filterDatesRange={filterDatesRange}
            setFilterDatesRange={setFilterDatesRange}
            selectStatus={SELECT_STATUS}
            setFilterParameter={setFilterParameter}
            filterParameter={filterParameter}
            rowInfo={selected}
            onValidateClick={handleOpenValidateDialog}
            handleExport={handleExport}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, minHeight: 380, overflow: 'auto' }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={DataSourceListData?.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  parent="dataSource"
                  isCheckBox="true"
                />
                <TableBody>
                  {DataSourceListData?.length > 0 &&
                    DataSourceListData?.map((item, index) => {
                      const isItemSelected = selected.indexOf(item) !== -1;

                      return (
                        <StyledTableRow
                          hover
                          key={index}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                        >
                          <TableCell padding="checkbox" sx={{ pl: 2.5 }}>
                            <Checkbox
                              checked={isItemSelected}
                              onChange={(event) => handleClick(event, item)}
                              sx={{
                                color: '#cbd5e1',
                                '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                              }}
                            />
                          </TableCell>
                          <StyledTableCell align="left" sx={{ fontWeight: 600, color: '#0284c7' }}>
                            {item.datasetName ? item.datasetName : 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                            {item.importMethod ? item.importMethod : 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell align="left" sx={{ color: '#0f172a', fontWeight: 600 }}>
                            {item.noOfRecords ? item.noOfRecords : 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                            {item.totalColumn ? item.totalColumn : 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                            {item.uploadedOn ? fDate(item.uploadedOn) : 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                            {item.modifiedOn ? fDate(item.modifiedOn) : 'N/A'}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <Label
                              variant="ghost"
                              color={getLabelColor(item.status)}
                              sx={{ textTransform: 'capitalize' }}
                            >
                              {item.status || 'N/A'}
                            </Label>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <UserMoreMenu
                              partner={item}
                              rowId={item.id}
                              parent="data"
                              bankId={item.bankId}
                              bankName={item.bankName}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
                {DataSourceListData?.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <StyledTableCell
                        align="center"
                        colSpan={TABLE_HEAD.length + 1}
                        sx={{ py: 3 }}
                      >
                        <SearchNotFoundAgreement searchQuery={filterName} />
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Modern Pagination Footer */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2, px: 3, borderTop: '1px solid #f1f5f9', bgcolor: '#ffffff', gap: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Showing</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                {DataSourceListData?.length || 0} records
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                Page <span style={{ fontWeight: 600, color: '#0f172a' }}>{page + 1}</span>
              </Typography>

              <Stack direction="row" spacing={1}>
                <IconButton
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  disabled={!hasNextPage}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </Card>

        <Dialog
          fullWidth
          open={validateDialogOpen}
          onClose={() => setValidateDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a' }}>
            Validate Data
          </DialogTitle>

          <DialogContent dividers>
            <Typography sx={{ color: '#64748b', fontSize: '0.95rem' }}>
              Are you sure you want to validate this data?
            </Typography>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setValidateDialogOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                borderColor: '#e2e8f0',
                color: '#637381',
                '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleConfirmValidate}
              sx={{
                bgcolor: '#0284c7',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 4px 14px rgba(2,132,199,0.25)',
                '&:hover': { bgcolor: '#0369a1' }
              }}
            >
              Yes, Validate
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
